/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {DOCUMENT} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {tap} from 'rxjs/operators';

import {MenuDropdownService} from './navigation/aside/dropdown/dropdown.component';
import {AuthenticationEvent, AuthenticationService} from './shared/authentication';
import {IdleSessionService} from './shared/authentication/idle.service';
import {RolesService} from './shared/authentication/roles/roles.service';
import {CookieService} from './shared/cookie-service/cookie.service';
import {FiqlService} from './shared/fiql/fiql.service';
import {I18nManager} from './shared/i18n/i18n.module';
import {LocalizationService} from './shared/localization/localization.service';
import {ModalService} from './shared/modal';
import {isBrowserSupported, isMobileDevice} from './shared/utils/browser';

const DEBUG_CONFIDENTIALITY = 'gp_debug_confidentiality';

/*
 * App Component
 * Top Level Component
 */
@Component({
  selector: 'app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MenuDropdownService,
    FiqlService
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  public name: string = 'Globe Payroll';
  public showChatbot = false;

  public emptyBackground: boolean = false;
  public isOnlyNotifications: boolean = true;
  public isAccessLimited: boolean = false;
  public isMobile: boolean = false;
  public isLandscapeOrientation: boolean = false;
  private removeListener: () => void;

  // Force instantiation/initialization of those components
  constructor(private rolesService: RolesService,
              private l10nService: LocalizationService,
              private i18: I18nManager,
              private modalService: ModalService,
              private router: Router,
              private renderer: Renderer2,
              private toastrService: ToastrService,
              private translate: TranslateService,
              private authenticationService: AuthenticationService,
              private cookieService: CookieService,
              private idleService: IdleSessionService,
              @Inject(DOCUMENT) private document) {

    router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        modalService.closeAllModals();
      }
    });

    if (localStorage.getItem('presentationMode') === 'true') {
      renderer.addClass(document.body, 'presentation-mode');
    }
  }

  ngOnInit() {
    this.isMobile = isMobileDevice();
    this.handleOrientationChange();
    this.authenticationService.authenticationEvents
      .pipe(
        tap(result => {
          if (result.event === AuthenticationEvent.TOKEN_VERIFIED
            || result.event === AuthenticationEvent.LOGIN_SUCCESSFUL) {
            this.emptyBackground = localStorage.getItem('emptyBackground') !== 'false';
          } else if (result.event === AuthenticationEvent.LOGOUT) {
            this.emptyBackground = false;
          }
        }))
      .subscribe();

    if (!isBrowserSupported()) {
      this.translate.get('COMMON.browser_not_supported')
        .subscribe(value => this.toastrService.error(value));
    }

    this.isOnlyNotifications = this.rolesService.isOnlyNotifications();
    this.isAccessLimited = this.rolesService.isAccessLimited();

    if (localStorage.getItem(DEBUG_CONFIDENTIALITY) === 'true') {
      this.cookieService.setCookie(DEBUG_CONFIDENTIALITY, 'true', new Date('3000-01-01'), '/');
    } else {
      this.cookieService.deleteCookie(DEBUG_CONFIDENTIALITY, '/');
    }
  }

  private handleOrientationChange(): void {
    const screenOrientation = screen && screen.orientation;

    if (screenOrientation && typeof screenOrientation.addEventListener === 'function') {
      const listener = () => this.setLandscapeOrientation(screenOrientation.type.startsWith('landscape'));

      listener();
      screenOrientation.addEventListener('change', listener);
      this.removeListener = () => screenOrientation.removeEventListener('change', listener);

      return;
    }

    // Fallback for browsers without the Screen Orientation API.
    const mediaQueryList = matchMedia('(orientation: portrait)');
    const listener = () => this.setLandscapeOrientation(!mediaQueryList.matches);

    listener();
    mediaQueryList.addEventListener('change', listener);

    this.removeListener = () => mediaQueryList.removeEventListener('change', listener);
  }

  private setLandscapeOrientation(isLandscape: boolean): void {
    this.isLandscapeOrientation = isLandscape;
  }

  ngOnDestroy() {
    if (this.removeListener) {
      this.removeListener();
    }
  }
}
